import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Paperclip,
  Trash2,
  Loader2,
  Plus,
  UserCheck,
  UserX,
  UserPlus,
  Users,
  LogOut,
  X,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  useDevIssue,
  useUpdateDevIssue,
  useResolveDevIssue,
  useWontFixDevIssue,
  useReopenDevIssue,
  useTakeDevIssue,
  useUnassignDevIssue,
  useUploadDevIssueAttachment,
  useDeleteDevIssueAttachment,
  useDeleteDevIssue,
  useAddDevIssueParticipant,
  useRemoveDevIssueParticipant,
} from "@/features/dev_issues/hooks/useDevIssues";
import { IssueStatusBadge } from "@/features/dev_issues/components/IssueStatusBadge";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useConversationDeepLink } from "@/hooks/useConversationDeepLink";
import { IssuePriorityBadge } from "@/features/dev_issues/components/IssuePriorityBadge";
import { IssueCommentThread } from "@/features/dev_issues/components/IssueCommentThread";
import { AttachmentLink } from "@/features/dev_issues/components/AttachmentLink";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { devIssuesApi } from "@/features/dev_issues/api/devIssues";
import DOMPurify from "dompurify";
import {
  ISSUE_CATEGORY_LABELS,
  ISSUE_PRIORITY_LABELS,
} from "@/features/dev_issues/types";
import type { IssueCategory, IssuePriority } from "@/features/dev_issues/types";

const ALLOWED_TAGS = ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"];
const ALLOWED_ATTR = ["src", "alt", "width", "height", "start"];

function sanitizeDesc(html: string) {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

function DescriptionContent({ content }: { content: string }) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement;
    if (t.tagName === "IMG") setLightbox((t as HTMLImageElement).src);
  }, []);

  if (!content) {
    return <p className="text-sm text-gray-400 dark:text-gray-500 italic">Brak opisu.</p>;
  }

  if (content.trimStart().startsWith("<")) {
    return (
      <>
        <div
          className="rich-text-content"
          dangerouslySetInnerHTML={{ __html: sanitizeDesc(content) }}
          onClick={handleClick}
        />
        {lightbox && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={() => setLightbox(null)}
          >
            <img
              src={lightbox}
              alt=""
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute top-4 right-4 text-white text-2xl font-bold hover:text-gray-300"
              onClick={() => setLightbox(null)}
            >
              ×
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
      {content}
    </p>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface ParticipantsSectionProps {
  issueId: string;
  reporterId: string | null;
  participants: { id: string; full_name: string }[];
  currentUserId: string | null | undefined;
  canManage: boolean;
}

function ParticipantsSection({
  issueId,
  reporterId,
  participants,
  currentUserId,
  canManage,
}: ParticipantsSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: allUsers = [] } = useInternalUsers();
  const { mutate: addParticipant, isPending: adding } = useAddDevIssueParticipant(issueId);
  const { mutate: removeParticipant, isPending: removing } = useRemoveDevIssueParticipant(issueId);

  const isParticipant = participants.some((p) => p.id === currentUserId);
  const canLeave = !!currentUserId && isParticipant && reporterId !== currentUserId;

  const takenIds = useMemo(() => {
    const ids = new Set(participants.map((p) => p.id));
    if (reporterId) ids.add(reporterId);
    return ids;
  }, [participants, reporterId]);

  const suggestions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allUsers
      // Defensive guard: backend rejects contractors from participating, and
      // /users/ already excludes them by default — but if a stray contractor
      // ever appears in the list, drop them here so the user can't try.
      .filter((u) => u.role !== "contractor")
      .filter((u) => !takenIds.has(u.id))
      .filter(
        (u) =>
          !term ||
          u.full_name.toLowerCase().includes(term) ||
          u.username.toLowerCase().includes(term)
      )
      .slice(0, 8);
  }, [allUsers, takenIds, search]);

  function handlePickUser(userId: string) {
    setAddError(null);
    addParticipant(userId, {
      onSuccess: () => {
        setPickerOpen(false);
        setSearch("");
      },
      onError: (err: unknown) => {
        // Most likely a 400 — usually "only internal users may participate"
        // (race with role change) or another backend guard. Surface verbatim
        // so the user knows what happened.
        const e = err as { response?: { data?: { errors?: string } } };
        setAddError(
          e?.response?.data?.errors ||
            "Nie udało się dodać uczestnika. Spróbuj ponownie."
        );
      },
    });
  }

  useEffect(() => {
    if (!pickerOpen) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [pickerOpen]);

  function handleLeave() {
    if (!currentUserId) return;
    if (!window.confirm("Stracisz dostęp do tego zgłoszenia z listy. Kontynuować?")) return;
    removeParticipant(currentUserId);
  }

  return (
    <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-2 mb-2">
        <Users size={14} className="text-gray-400 dark:text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Uczestnicy dyskusji
        </h2>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          ({participants.length})
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {participants.length === 0 && (
          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
            Nikt poza zgłaszającym nie jest jeszcze w dyskusji.
          </span>
        )}

        {participants.map((p) => (
          <span
            key={p.id}
            className="inline-flex items-center gap-1.5 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs px-2.5 py-1 rounded-full"
          >
            {p.full_name}
            {canManage && (
              <button
                type="button"
                onClick={() => removeParticipant(p.id)}
                disabled={removing}
                aria-label={`Usuń ${p.full_name} z dyskusji`}
                title="Usuń z dyskusji"
                className="text-violet-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-40"
              >
                <X size={11} />
              </button>
            )}
          </span>
        ))}

        {canManage && (
          <div className="relative" ref={containerRef}>
            <button
              type="button"
              onClick={() => {
                setPickerOpen((v) => !v);
                setSearch("");
              }}
              disabled={adding}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-dashed border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:opacity-40"
            >
              {adding ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />}
              Dodaj uczestnika
            </button>

            {pickerOpen && (
              <div className="absolute left-0 sm:left-auto z-20 mt-1 w-[min(18rem,calc(100vw-2rem))] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2">
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Szukaj osoby…"
                  className="w-full border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                />
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 px-0.5">
                  Tylko pracownicy GK ELEKTIM. Podwykonawcy nie mogą uczestniczyć w dyskusji.
                </p>
                <ul className="mt-1 max-h-56 overflow-y-auto">
                  {suggestions.length === 0 ? (
                    <li className="text-xs text-gray-400 dark:text-gray-500 italic px-2 py-2">
                      Brak osób do dodania.
                    </li>
                  ) : (
                    suggestions.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          onClick={() => handlePickUser(u.id)}
                          className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                        >
                          {u.full_name}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {addError && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          {addError}
        </p>
      )}

      {canLeave && (
        <div className="mt-3">
          <button
            type="button"
            onClick={handleLeave}
            disabled={removing}
            className="inline-flex items-center gap-1.5 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-40"
          >
            {removing ? <Loader2 size={11} className="animate-spin" /> : <LogOut size={11} />}
            Opuść dyskusję
          </button>
        </div>
      )}
    </div>
  );
}

export function DevIssueDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isDev = user?.is_developer || user?.is_ceo;

  const { data: issue, isLoading } = useDevIssue(id);
  usePageTitle(issue?.title);
  // Powiadomienie o komentarzu prowadzi do rozmowy pod zgłoszeniem.
  const { ref: commentsRef } = useConversationDeepLink<HTMLDivElement>(!isLoading && !!issue);
  const { mutate: updateIssue, isPending: updating } = useUpdateDevIssue(id);
  const { mutate: resolve, isPending: resolving } = useResolveDevIssue();
  const { mutate: wontFix, isPending: wontFixing } = useWontFixDevIssue();
  const { mutate: reopen, isPending: reopening } = useReopenDevIssue();
  const { mutate: take, isPending: taking } = useTakeDevIssue();
  const { mutate: unassign, isPending: unassigning } = useUnassignDevIssue();
  const { mutate: uploadAttachment, isPending: uploadingAttachment } = useUploadDevIssueAttachment(id);
  const { mutate: deleteAttachment, isPending: deletingAttachment } = useDeleteDevIssueAttachment(id);

  const { mutate: deleteIssue, isPending: deleting } = useDeleteDevIssue();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [editDescription, setEditDescription] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400 dark:text-gray-500">
        <p className="text-sm">Nie znaleziono zgłoszenia.</p>
        <button
          onClick={() => navigate("/dev-issues")}
          className="text-sm text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline"
        >
          Wróć do listy
        </button>
      </div>
    );
  }

  const isOpen = issue.status === "open" || issue.status === "in_progress";
  const isResolved = issue.status === "resolved";
  const isWontFix = issue.status === "wont_fix";
  const canAct = isDev;
  // Reporter can delete their own issue only while it isn't resolved; CEO always can.
  const canDelete = !!user?.is_ceo || (issue.reporter_id === user?.id && !isResolved);

  const selectCls =
    "border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] disabled:opacity-50";

  return (
    <div className="flex flex-col gap-4 sm:gap-5 max-w-3xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate("/dev-issues")}
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 w-fit"
      >
        <ArrowLeft size={15} />
        Wróć do listy
      </button>

      {/* Main card */}
      <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden rounded-lg sm:rounded-none">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-start gap-3 flex-wrap">
            {/* Title — editable by reporter and devs while open */}
            <div className="flex-1 min-w-0">
              {editTitle ? (
                <div className="flex gap-2 items-center">
                  <input
                    autoFocus
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        updateIssue({ title: titleDraft.trim() }, { onSuccess: () => setEditTitle(false) });
                      }
                      if (e.key === "Escape") setEditTitle(false);
                    }}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                  <button
                    onClick={() => updateIssue({ title: titleDraft.trim() }, { onSuccess: () => setEditTitle(false) })}
                    disabled={updating}
                    className="text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline disabled:opacity-40"
                  >
                    Zapisz
                  </button>
                  <button
                    onClick={() => setEditTitle(false)}
                    className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Anuluj
                  </button>
                </div>
              ) : (
                <h1
                  className={`text-xl font-bold text-gray-900 dark:text-gray-100 ${isOpen ? "cursor-pointer hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)]" : ""}`}
                  title={isOpen ? "Kliknij aby edytować" : undefined}
                  onClick={() => {
                    if (!isOpen) return;
                    setTitleDraft(issue.title);
                    setEditTitle(true);
                  }}
                >
                  {issue.title}
                </h1>
              )}
            </div>

            <IssueStatusBadge status={issue.status} className="mt-1 flex-shrink-0" />
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-gray-500 dark:text-gray-400">
            <span>Zgłoszone przez: <strong className="text-gray-700 dark:text-gray-300">{issue.reporter_name ?? "Nieznany"}</strong></span>
            <span>Data: <strong className="text-gray-700 dark:text-gray-300">{formatDate(issue.created_at)}</strong></span>
            {issue.assignee_name && (
              <span>Przyjęte przez: <strong className="text-gray-700 dark:text-gray-300">{issue.assignee_name}</strong></span>
            )}
            {issue.resolved_at && (
              <span>Rozwiązano: <strong className="text-gray-700 dark:text-gray-300">{formatDate(issue.resolved_at)}</strong></span>
            )}
          </div>
        </div>

        {/* Developer fields — category, priority, assignment */}
        {canAct && (
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 bg-violet-50/40 dark:bg-violet-900/10">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wide">Dev</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
              {/* Priority select */}
              <div className="flex items-center gap-2 min-w-0">
                <label className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 w-20 sm:w-auto">Priorytet</label>
                <select
                  value={issue.priority ?? ""}
                  disabled={updating}
                  onChange={(e) =>
                    updateIssue({ priority: (e.target.value as IssuePriority) || null })
                  }
                  className={`${selectCls} flex-1 sm:flex-none`}
                >
                  <option value="">Nie ustawiono</option>
                  {Object.entries(ISSUE_PRIORITY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Category select */}
              <div className="flex items-center gap-2 min-w-0">
                <label className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 w-20 sm:w-auto">Kategoria</label>
                <select
                  value={issue.category ?? ""}
                  disabled={updating}
                  onChange={(e) =>
                    updateIssue({ category: (e.target.value as IssueCategory) || null })
                  }
                  className={`${selectCls} flex-1 sm:flex-none`}
                >
                  <option value="">Nie ustawiono</option>
                  {Object.entries(ISSUE_CATEGORY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Assignment */}
              <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
                {issue.assignee_name && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Przydzielono:{" "}
                    <span className="font-medium text-violet-700 dark:text-violet-300">
                      {issue.assignee_name}
                    </span>
                  </span>
                )}
                {!issue.assignee_id && isOpen && (
                  <button
                    disabled={taking}
                    onClick={() => take(id)}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-medium disabled:opacity-50 transition-colors"
                  >
                    {taking ? <Loader2 size={12} className="animate-spin" /> : <UserCheck size={12} />}
                    Przejmij
                  </button>
                )}
                {issue.assignee_id && isOpen && (
                  <button
                    disabled={unassigning}
                    onClick={() => unassign(id)}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50 transition-colors"
                  >
                    {unassigning ? <Loader2 size={12} className="animate-spin" /> : <UserX size={12} />}
                    Porzuć
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Opis</h2>
            {isOpen && !editDescription && (
              <button
                onClick={() => { setDescDraft(issue.description); setEditDescription(true); }}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)]"
              >
                Edytuj
              </button>
            )}
          </div>

          {editDescription ? (
            <div className="flex flex-col gap-2">
              <RichTextEditor
                value={descDraft}
                onChange={setDescDraft}
                disabled={updating}
                placeholder="Opisz dokładnie co się dzieje…"
                onImageUpload={(file) => devIssuesApi.uploadDescriptionImage(file)}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => updateIssue({ description: descDraft.trim() }, { onSuccess: () => setEditDescription(false) })}
                  disabled={updating || !descDraft.trim()}
                  className="text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline disabled:opacity-40"
                >
                  Zapisz
                </button>
                <button
                  onClick={() => setEditDescription(false)}
                  className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Anuluj
                </button>
              </div>
            </div>
          ) : (
            <DescriptionContent content={issue.description} />
          )}
        </div>

        {/* Discussion participants — visible to everyone with access */}
        <ParticipantsSection
          issueId={id}
          reporterId={issue.reporter_id}
          participants={issue.participants}
          currentUserId={user?.id}
          canManage={canAct || issue.reporter_id === user?.id}
        />

        {/* Non-dev summary row (visible to reporters) */}
        {!canAct && (
          <div className="px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Priorytet:</span>
              <IssuePriorityBadge priority={issue.priority} />
            </div>
            {issue.category && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Kategoria:</span>
                <span className="text-xs text-gray-700 dark:text-gray-300">{ISSUE_CATEGORY_LABELS[issue.category]}</span>
              </div>
            )}
          </div>
        )}

        {/* Attachments */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Załączniki</h2>
            {isOpen && (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploadingAttachment}
                className="flex items-center gap-1 text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline disabled:opacity-40"
              >
                {uploadingAttachment ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Plus size={12} />
                )}
                Dodaj
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={(e) => {
                if (!e.target.files) return;
                Array.from(e.target.files).forEach((file) => uploadAttachment(file));
                e.target.value = "";
              }}
            />
          </div>

          {issue.attachments.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">Brak załączników.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {issue.attachments.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700/50 px-3 py-1.5 text-xs"
                >
                  <Paperclip size={11} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <AttachmentLink
                    attachment={a}
                    className="text-[var(--color-primary)] dark:text-[var(--color-primary)] max-w-[190px] flex items-center gap-1.5 min-w-0"
                    nameClassName="truncate"
                  />
                  {isOpen && (
                    // Kosz odsunięty od ikony pobrania (odstęp + kreska), żeby nie kasować
                    // załącznika przy próbie pobrania.
                    <button
                      onClick={() => deleteAttachment(a.id)}
                      disabled={deletingAttachment}
                      title="Usuń załącznik"
                      className="text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-40 ml-2 pl-2 border-l border-gray-200 dark:border-gray-600"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Action buttons — developer/director only */}
        {canAct && (
          <div className="px-4 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:flex-wrap gap-2">
            {isOpen && (
              <>
                <button
                  onClick={() => resolve(id)}
                  disabled={resolving}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
                >
                  {resolving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                  Oznacz jako rozwiązane
                </button>
                <button
                  onClick={() => wontFix(id)}
                  disabled={wontFixing}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 disabled:opacity-40 transition-colors"
                >
                  {wontFixing ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />}
                  Nie naprawiamy
                </button>
              </>
            )}
            {(isResolved || isWontFix) && (
              <button
                onClick={() => reopen(id)}
                disabled={reopening}
                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40 transition-colors"
              >
                {reopening ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Otwórz ponownie
              </button>
            )}
          </div>
        )}

        {/* Delete — reporter (own) or director (any) */}
        {canDelete && (
          <div className="px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
            {confirmDelete ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <span className="text-sm text-gray-600 dark:text-gray-300">Na pewno usunąć to zgłoszenie?</span>
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => deleteIssue(id, { onSuccess: () => navigate("/dev-issues") })}
                    disabled={deleting}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors"
                  >
                    {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                    Usuń
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 text-sm text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
              >
                <Trash2 size={14} />
                Usuń zgłoszenie
              </button>
            )}
          </div>
        )}

        {/* Comments */}
        <div ref={commentsRef} className="scroll-mt-4 px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1 h-5 rounded bg-[var(--color-primary)] flex-shrink-0" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Rozmowa</h2>
          </div>
          <IssueCommentThread issueId={id} />
        </div>
      </div>
    </div>
  );
}
